/** Formatting the same values the same way everywhere. */

export function shortId(id: string | null | undefined, keep = 8): string {
  if (!id) return "—";
  const body = id.includes("_") ? id.split("_").slice(1).join("_") : id;
  return body.length <= keep ? id : `${id.slice(0, id.indexOf("_") + 1)}${body.slice(-keep)}`;
}

export function bytes(value: number | null | undefined): string {
  if (value == null) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let size = value;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size < 10 && unit > 0 ? size.toFixed(1) : Math.round(size)} ${units[unit]}`;
}

export function mib(value: number | null | undefined): string {
  if (value == null) return "—";
  return value >= 1024 ? `${(value / 1024).toFixed(1)} GiB` : `${value} MiB`;
}

export function dateTime(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

export function dateOnly(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** "3 minutes ago" / "in 12 days". Undated is "—", not "55 years ago". */
export function relative(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return "—";

  const delta = date.getTime() - Date.now();
  const magnitude = Math.abs(delta);
  const [amount, unit]: [number, Intl.RelativeTimeFormatUnit] =
    magnitude < MINUTE ? [Math.round(delta / 1000), "second"]
    : magnitude < HOUR ? [Math.round(delta / MINUTE), "minute"]
    : magnitude < DAY ? [Math.round(delta / HOUR), "hour"]
    : magnitude < 30 * DAY ? [Math.round(delta / DAY), "day"]
    : [Math.round(delta / (30 * DAY)), "month"];

  return new Intl.RelativeTimeFormat(undefined, { numeric: "auto" }).format(amount, unit);
}

export function duration(seconds: number | null | undefined): string {
  if (seconds == null) return "—";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${Math.round(seconds % 60)}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

export function titleCase(value: string | null | undefined): string {
  if (!value) return "—";
  return value.replace(/[_-]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Days until a date, negative once past. */
export function daysUntil(value: string | null | undefined): number | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return Math.trunc((date.getTime() - Date.now()) / DAY);
}
